import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import router, { useRouter } from 'next/router';
import db from '@services/firebase-service';
import {
  onSnapshot,
  query as firebaseQuery,
  collection,
  updateDoc,
  doc,
} from 'firebase/firestore';
import { updateQuickQuestion } from '@services/quick-questions-service';
import Image from '@atoms/Image';
import QuickQuestionItem, { GroupedQuickQuestionItem } from '@molecules/QuickQuestionItem';
import {
  updateEngageType,
  updateLivestreamSession
} from '@services/youtube-platform.service';
import NextImage from 'next/image';
import EmptyQuickQuestions from '@public/images/empty_quick_questions.svg';
import { ButtonGroup, Button } from '@nextui-org/button';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger
} from '@nextui-org/react';
import { groupBy, reduce } from 'lodash';
import { computeLuminance } from '@lib/interactionHelper';
import { getLiveLayoutColors } from '@components/RunOfShow/helper/presentationLive';
import useFonts from '@lib/hooks/useFonts';

const baseOptions = ['All questions', 'Answered', 'Unanswered'];

const QuickQuestions = forwardRef(({
  streamId,
  isResult = false,
  questionsData,
  moderationMode = false,
  platformType,
  currentRosFeature,
  isSandbox = false,
  livestreamData
}, ref) => {
  const [query, setQuery] = useState('');
  const [settings, setSettings] = useState({});

  const scrollContainerRef = useRef(null);

  const joinType = useRouter().query?.joinType;

  const [allQuickQuestions, setAllQuickQuestions] = useState([]);
  const [quickQuestions, setQuickQuestions] = useState([]);

  useEffect(() => {
    if (!allQuickQuestions?.length) return;
    const groupedQuestions = allQuickQuestions.filter((question) => question.parentQuestion);
    const filterQuestions = groupedQuestions.map((question) => question.parentQuestion);
    const unGroupedQuickQuestions = allQuickQuestions
      .filter((question) => !question.parentQuestion)
      .filter((question) => !filterQuestions.includes(question.message))
      .map((question) => ({
        ...question,
        username: settings?.quickQuestionShowUserName || settings?.showUserName ? question.username : 'Anonymous',
        tags: [question.tag]
      }));

    const groups = reduce(groupBy(groupedQuestions, 'parentQuestion'), (acc, questions, title) => {
      const parentQuestion = allQuickQuestions.filter((question) => question.message === title)[0];
      acc.push({
        title,
        ...parentQuestion,
        upvotes: parentQuestion.upvotes + questions.reduce((acc, question) => acc + question.upvotes, 0),
        users: Array.from(
          new Set([
            ...questions.map((question) => question.username)
          ])
        ).map((username) => settings?.quickQuestionShowUserName || settings?.showUserName ? username : 'Anonymous'),
        tags: Array.from(
          new Set([
            parentQuestion.tag,
            ...questions.map((question) => question.tag)
          ])
        ),
        questions: questions.map((question) => ({
          ...question,
          username: settings?.quickQuestionShowUserName || settings?.showUserName ? question.username : 'Anonymous'
        }))
      });
      return acc;
    }, [])
      .map((group, index) => ({ ...group, isDefaultExpanded: index === 0 }));

    setQuickQuestions(
      [...groups, ...unGroupedQuickQuestions]
        .sort((a, b) => b.createdAt - a.createdAt)
        .reverse()
        .sort((a, b) => b.upvotes - a.upvotes)
    );
  }, [allQuickQuestions, settings]);

  const handleMarkAnswered = async (id, firebase_comment_id, isChecked) => {
    await updateQuickQuestion(id, {
      isChecked,
      streamId
    });
    const docRef = doc(db, 'streams', streamId, 'quickQuestions', firebase_comment_id);
    await updateDoc(
      docRef,
      { isChecked }
    );
  };

  const handleDelete = async (id, firebase_comment_id) => {
    await updateQuickQuestion(id, {
      isDeleted: true,
      streamId
    });
    const docRef = doc(db, 'streams', streamId, 'quickQuestions', firebase_comment_id);

    await updateDoc(
      docRef,
      { isDeleted: true }
    );
  };

  useEffect(() => {
    if (!isResult) return;
    setAllQuickQuestions(questionsData);
  }, [isResult]);

  useEffect(() => {
    if (isResult) return;
    const unSubQuickQuestions = onSnapshot(
      firebaseQuery(
        collection(db, 'streams', streamId, 'quickQuestions'),
      ),
      (querySnapshot) => {
        const temp = [];
        const modified = [];

        querySnapshot.docChanges().forEach((change) => {
          if (!['added', 'modified'].includes(change.type)) return;
          const document = {
            ...change.doc.data(),
            firebase_comment_id: change.doc.id
          };

          if (change.type === 'modified') {
            modified.push(document);
          } else {
            temp.push(document);
          }
        });

        if (temp.length > 0) {
          setAllQuickQuestions(prev => [...prev, ...temp]);
        }

        if (modified.length > 0) {
          setAllQuickQuestions(prev => prev.map((question) => {
            const modifiedQuestion = modified.find((x) => x.id === question.id);
            if (modifiedQuestion) return modifiedQuestion;
            return question;
          }));
        }
      }
    );

    return () => {
      unSubQuickQuestions();
    };
  }, []);

  const reset = () => {
    setQuery('');
  };

  const handleQuery = (evt) => {
    setQuery(evt.currentTarget.value);
  };

  useEffect(() => {
    if (questionsData?.length > 0) return;
    if (isResult || moderationMode) return;
    const sessionId = localStorage.getItem('sessionId');
    updateEngageType(streamId, 'Quick Questions', { platformType });
    if (sessionId) updateLivestreamSession(sessionId, { engageType: 'Quick Questions', updateQuickQuestionCount: true });
  }, []);

  const stopCurrentFeature = () => {
    router.push({
      pathname: `/streams/${streamId}/ros`,
      query: joinType && { joinType }
    });
  };

  useImperativeHandle(ref, () => ({ stopCurrentFeature }));

  const getStyle = () => {
    const style = { height: '100%' };
    style.cursor = 'default';
    if (moderationMode) {
      style.height = 'calc(100vh - 266px)'; // + 48px height of Search
    } else if (isSandbox) {
      style.height = 'calc(100vh - 400px)';
    } else if (isResult) {
      delete style.height;
    }
    return style;
  };

  const bgImg = livestreamData?.settings?.quickQuestionImageSrc;
  const bgImgOpacity = livestreamData?.settings?.quickQuestionImageOpacity / 100;
  let bgColor = currentRosFeature?.color?.quickQuestionBgColor || '#212121';
  let textColor = currentRosFeature?.color?.quickQuestionTextColor || null;
  const { isBgLight } = computeLuminance(bgColor);
  const colors = getLiveLayoutColors(isBgLight ? 'light' : 'dark');
  const oppsiteColors = getLiveLayoutColors(!isBgLight ? 'light' : 'dark');
  const { getFontStyle } = useFonts();
  const { fontFamily } = getFontStyle(currentRosFeature?.setting?.FontStyle || 'Sans-Serif');

  useEffect(() => {
    if (!currentRosFeature?.setting) {
      setSettings(livestreamData?.settings);
      return;
    }
    setSettings(currentRosFeature.setting);
  }, [currentRosFeature, livestreamData]);
  const [filter, setFilter] = useState(baseOptions[0]);

  const getSelectedTag = () => {
    if (baseOptions.includes(filter)) return null;
    return filter;
  };

  const [tags, setTags] = useState([]);

  useEffect(() => {
    if (!allQuickQuestions.length > 0) return;

    const tags = allQuickQuestions.map((q) => q.tag);
    const uniqueTags = Array.from(new Set(tags));
    const tagsWithQuestionCount = uniqueTags.map((tag) => ({
      tag,
      count: allQuickQuestions.filter((q) => q.tag === tag).length
    }));
    const sortedTags = tagsWithQuestionCount.sort((a, b) => b.count - a.count);
    setTags(sortedTags);
  }, [allQuickQuestions]);

  return (
    <>
      <div className='h-full w-full' >
        {moderationMode && (
          <div className='relative flex items-center justify-between bg-gray-30 rounded-lg'>
            <Image src="/images/Search1.svg" className='ml-5' />
            <input
              placeholder="Search Entries"
              value={query}
              onChange={handleQuery}
              type="text"
              autoComplete='off'
              className={'w-full bg-gray-30 text-neutral-50 text-xs leading-3 font-normal pl-3 border border-gray-30 outline-none rounded-lg py-3.5'}
              name="search" />
            <button onClick={reset}>
              <Image src="/images/reload.svg" className='mr-5' />
            </button>
          </div>
        )}

        <div
          style={{ ...getStyle(), color: textColor || colors.textColor, fontFamily }}
          className={`px-5
          flex flex-col pb-5 ${isResult && '!px-3 !pt-3 !pb-4 aspect-video rounded-[16px] overflow-hidden'} relative`}
        >
          {bgImg && <NextImage
            style={{ opacity: bgImgOpacity }}
            src={`${process.env.NEXT_PUBLIC_BUCKET_URL}${bgImg}`}
            className=' object-cover rounded-lg'
            fill
            alt='background image'
          />}

          <div className='flex gap-2 mb-6'>
            {baseOptions.map((option) => (
              <Button
                size='sm'
                radius='full'
                onPress={() => setFilter(option)}
                className={`text-base py-2 px-4 ${filter === option ? `${oppsiteColors.containerColor} ${oppsiteColors.hoverBg} ${oppsiteColors.textColor}` : `${colors.containerColor} ${colors.textColor} ${colors.hoverBg}`}`}
              >
                {option}
              </Button>
            ))}

            {tags.length > 0 && <ButtonGroup>
              <Button
                size='sm'
                radius='full'
                className={`text-base py-2 px-4 ${filter === getSelectedTag() ? `${oppsiteColors.containerColor} ${oppsiteColors.hoverBg} ${oppsiteColors.textColor}` : `${colors.containerColor} ${colors.textColor} ${colors.hoverBg}`}`}
                onPress={() => {
                  const tag = getSelectedTag();

                  if (tag) {
                    setFilter('All questions');
                    return;
                  }

                  setFilter(tags[0].tag);
                }}
              >
                {getSelectedTag() || tags[0].tag}
              </Button>

              <Dropdown
                placement='bottom-end'
                selectionMode='single'
                selectedKeys={[filter]}
              >
                <DropdownTrigger>
                  <Button
                    size='sm'
                    radius='full'
                    isIconOnly
                    className={filter === getSelectedTag() ? `${oppsiteColors.containerColor} ${oppsiteColors.textColor} ${oppsiteColors.hoverBg}` : `${colors.containerColor} ${colors.textColor} ${colors.hoverBg}`}
                    startContent={<KeyboardArrowDownIcon fontSize='small' />}
                  />
                </DropdownTrigger>
                <DropdownMenu>
                  {tags.map((tag) => (
                    <DropdownItem key={tag} onPress={() => setFilter(tag.tag)}>
                      {tag.tag} ({tag.count})
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </ButtonGroup>}
          </div>

          <div className='overflow-y-scroll'>
            {quickQuestions
              .filter((question) => !question.isDeleted)
              .filter((question) => {
                if (!query) return true;

                if (question.title) {
                  const questions = [
                    question.title.toLowerCase(),
                    ...question.questions
                      .map(q => q.message.toLowerCase())
                  ];
                  return questions.some(q => q.includes(query.toLowerCase()));
                } else {
                  return question.message.toLowerCase().includes(query.toLowerCase());
                }
              })
              .filter((question) => {
                if (filter === 'All questions') return true;

                if (filter === 'Answered') return question.isChecked;
                if (filter === 'Unanswered') return !question.isChecked;
                const selectedTag = getSelectedTag();
                if (!selectedTag) return false;
                return question.tags.includes(selectedTag);
              })
              .map((question) => {
                if (!question.title) {
                  return (
                    <div className='mt-4 rounded-2xl overflow-hidden bg-black/15'>
                      <QuickQuestionItem
                        key={question.id}
                        isResult={isResult}
                        question={question}
                        handleDelete={handleDelete}
                        isBgLight={isBgLight}
                        handleMarkAnswered={handleMarkAnswered}
                      />
                    </div>
                  );
                }
                return (
                  <GroupedQuickQuestionItem
                    isResult={isResult}
                    group={question}
                    handleDelete={handleDelete}
                    handleMarkAnswered={handleMarkAnswered}
                    isBgLight={isBgLight}
                    isDefaultExpanded={question.isDefaultExpanded}
                  />
                );
              })}
            {allQuickQuestions?.length === 0 && (
              <div className="absolute  m-0 top-1/2 left-1/2 transform -translate-y-1/2 -translate-x-1/2 z-10">
                <div className='flex flex-col items-center' style={{ color: textColor }}>
                  <EmptyQuickQuestions />

                  <div className='mt-4'>
                    <div className='flex justify-center items-center mb-4'>
                      <span className='text-center text-xl font-semibold'>No questions yet</span><br />
                    </div>
                    <div className='flex justify-center items-center'>
                      <span className='text-center text-lg'>Type in the chat and your questions<br /> will appear here.</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div >
    </>
  );
});

export default QuickQuestions;
